<template>
  <div class="nt-scroll-popup">
    <transition name="nt-scroll-popup-fade">
      <div v-if="isOpen" class="nt-scroll-popup__overlay" @click.self="close">
        <dialog
          open
          class="nt-scroll-popup__dialog"
          :class="{ 'nt-scroll-popup--has-image': imageDesktop }"
          :style="dialogStyles"
          :aria-labelledby="titleId"
          aria-modal="true"
          tabindex="-1"
          ref="dialog"
          @keydown="handleDialogKeydown"
        >
          <button
            class="nt-scroll-popup__close"
            type="button"
            :aria-label="closeLabel"
            @click="closeWithGTM"
            ref="closeBtn"
          >
            <c-icon name="minus" size="sm" :color="imageDesktop ? '#1d2822' : undefined" />
          </button>

          <div v-if="imageDesktop" class="nt-scroll-popup__image">
            <c-image
              :image-mobile="imageMobile"
              :image-tablet="imageTablet"
              :image-desktop="imageDesktop"
              :alt="altText"
            />
          </div>

          <!-- Content body -->
          <div
            class="nt-scroll-popup__body"
            :class="[
              { 'nt-scroll-popup__body--no-image': !imageDesktop },
              `nt-scroll-popup__body--align-${bodyAlign}`
            ]"
          >
            <c-title
              :id="titleId"
              size="xs"
              weight="400"
              class="nt-scroll-popup__title"
              :align="bodyAlign"
            >
              <slot name="title" />
            </c-title>

            <c-text
              size="sm"
              weight="300"
              class="nt-scroll-popup__text"
              :align="bodyAlign"
            >
              <slot name="text" />
            </c-text>

            <c-button
              v-if="$slots.cta"
              class="nt-scroll-popup__cta"
              :href="ctaHref"
              :bg-color="ctaBgColor"
              :color="ctaColor"
              :radius="ctaRadius"
              :target="ctaTarget"
              :download="ctaDownload"
              @click.native="onCtaClick"
            >
              <slot name="cta" />
            </c-button>
          </div>
        </dialog>
      </div>
    </transition>

    <!-- FAB – Persistent floating button -->
    <transition :name="fabAnimated ? 'nt-scroll-popup-fab' : ''">
      <button
        v-if="showFab"
        class="nt-scroll-popup__fab"
        :class="{
          'nt-scroll-popup__fab--rolling': isRolling,
          'nt-scroll-popup__fab--switching': isSwitching,
          'nt-scroll-popup__fab--icononly': effectiveFabMode === 'icononly',
          'nt-scroll-popup__fab--no-icon': !hasFabIcon,
        }"
        type="button"
        :aria-label="fabLabel"
        @click="handleFabClick"
        ref="fabBtn"
      >
        <span
          v-if="hasFabIcon"
          class="nt-scroll-popup__fab-icon"
          aria-hidden="true"
          @animationend="onIconAnimEnd"
        >
          <span class="nt-scroll-popup__fab-svg nt-scroll-popup__fab-svg--plus">
            <slot name="fab-icon">
              <slot name="fab-icon-active" />
            </slot>
          </span>
          <span class="nt-scroll-popup__fab-svg nt-scroll-popup__fab-svg--down">
            <slot name="fab-icon-active">
              <slot name="fab-icon" />
            </slot>
          </span>
        </span>
        <span class="nt-scroll-popup__fab-label">{{ fabLabel }}</span>
      </button>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CButton from '../../00_v2/button/button.vue'
import CIcon from '../../00_v2/icon/icon.vue'
import CImage from '../../00_v2/image/image.vue'
import CTitle from '../../00_v2/title/title.vue'
import CText from '../../00_v2/text/text.vue'

export default {
  name: 'nt-scroll-popup',
  components: {
    CButton,
    CIcon,
    CImage,
    CTitle,
    CText,
  },
  props: {
    bgColor: {
      type: String,
      default: '#ffffff',
    },
    imageMobile: {
      type: String,
      default: '',
    },
    imageTablet: {
      type: String,
      default: '',
    },
    imageDesktop: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
    scrollTrigger: {
      type: Number,
      default: 50,
    },
    autoOpenOnScroll: {
      type: Boolean,
      default: true,
    },
    fabLabel: {
      type: String,
      default: 'Explore niobium technical solutions',
    },
    closeLabel: {
      type: String,
      default: 'Close dialog',
    },
    defaultOpen: {
      type: Boolean,
      default: false,
    },
    defaultShowFab: {
      type: Boolean,
      default: false,
    },
    enableFab: {
      type: Boolean,
      default: true,
    },
    fabAnimated: {
      type: Boolean,
      default: true,
    },
    fabMode: {
      type: String,
      default: 'pill',
      validator: (v) => ['pill', 'icononly'].includes(v),
    },
    bodyAlign: {
      type: String,
      default: 'center',
      validator: (v) => ['left', 'center', 'right'].includes(v),
    },
    ctaHref: {
      type: String,
      default: '#',
    },
    ctaBgColor: {
      type: String,
      default: '#88D49E',
    },
    ctaColor: {
      type: String,
      default: '#1d2822',
    },
    ctaRadius: {
      type: Number,
      default: 40,
    },
    ctaTarget: {
      type: String,
      default: '_self',
    },
    ctaDownload: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      uid: `nt-scroll-popup-${Math.random().toString(36).substr(2, 9)}`,
      isOpen: this.defaultOpen,
      hasTriggered: this.defaultOpen || this.defaultShowFab,
      isRolling: false,
      isSwitching: false,
      previousFocus: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    }
  },
  computed: {
    ...mapGetters(['scrollY']),
    titleId() {
      return `${this.uid}-title`
    },
    showFab() {
      return this.enableFab && this.hasTriggered && !this.isOpen
    },
    dialogStyles() {
      return {
        backgroundColor: this.bgColor,
      }
    },
    hasFabIcon() {
      return !!(this.$slots['fab-icon'] || this.$slots['fab-icon-active'])
    },
    effectiveFabMode() {
      if (!this.hasFabIcon) return 'pill'
      return this.windowWidth < 768 ? 'icononly' : this.fabMode
    },
    scrollThresholdPx() {
      if (typeof window === 'undefined') return Infinity
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight
      return scrollableHeight * (this.scrollTrigger / 100)
    },
    ctaText() {
      if (!this.$slots.cta) return null
      return this.$slots.cta.map(vn => vn.text || '').join('').trim() || null
    },
  },
  watch: {
    scrollY(value) {
      if (this.autoOpenOnScroll && !this.hasTriggered && value >= this.scrollThresholdPx) {
        this.open()
      }
    },
    isOpen(isNowOpen) {
      if (isNowOpen) {
        document.documentElement.classList.add('nt-scroll-popup--body-lock')

        this.$nextTick(() => {
          if (this.$refs.dialog) {
            this.$refs.dialog.focus()
          }
        })
      } else {
        document.documentElement.classList.remove('nt-scroll-popup--body-lock')

        this.$nextTick(() => {
          if (this.$refs.fabBtn) {
            this.$refs.fabBtn.focus()
          } else if (this.previousFocus) {
            this.previousFocus.focus()
          }
        })
      }
    },
  },
  beforeDestroy() {
    if (document.documentElement.classList.contains('nt-scroll-popup--body-lock')) {
      document.documentElement.classList.remove('nt-scroll-popup--body-lock')
    }
    window.removeEventListener('resize', this.handleResize)
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)

    const params = new URLSearchParams(window.location.search)
    if (params.get('success')) {
      this.hasTriggered = true
    }
    if (this.isOpen) {
      document.documentElement.classList.add('nt-scroll-popup--body-lock')
      this.$nextTick(() => {
        if (this.$refs.dialog) {
          this.$refs.dialog.focus()
        }
      })
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    handleFabClick() {
      if (this.isRolling || this.isSwitching) return
      this.pushGTM()

      if (!this.fabAnimated || !this.hasFabIcon) {
        this.open()
        return
      }

      if (this.effectiveFabMode === 'icononly') {
        this.isSwitching = true
        return
      }

      const btn = this.$refs.fabBtn
      const icon = btn && btn.querySelector('.nt-scroll-popup__fab-icon')
      if (btn && icon) {
        const btnRect = btn.getBoundingClientRect()
        const iconRect = icon.getBoundingClientRect()
        const distance = Math.max(btnRect.right - iconRect.right - 6, 0)
        btn.style.setProperty('--fab-roll-distance', `${distance}px`)
      }
      this.isRolling = true
    },
    onIconAnimEnd(event) {
      if (event.animationName === 'nt-fab-roll') {
        this.isRolling = false
        this.open()
      } else if (event.animationName === 'nt-fab-icon-switch') {
        this.isSwitching = false
        this.open()
      }
    },
    open() {
      this.previousFocus = document.activeElement
      this.isOpen = true
      this.hasTriggered = true
      this.$emit('open')
    },
    close() {
      this.isOpen = false
      this.$emit('close')
    },
    getFocusableElements() {
      if (!this.$refs.dialog) return []

      const selector =
        'a[href], button:not([disabled]), textarea:not([disabled]), ' +
        'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

      return Array.from(this.$refs.dialog.querySelectorAll(selector)).filter(
        (el) => el.offsetParent !== null
      )
    },
    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        this.close()
        return
      }

      if (event.key !== 'Tab') return

      const focusable = this.getFocusableElements()
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    closeWithGTM() {
      this.pushCloseGTM()
      this.close()
    },
    onCtaClick() {
      if (this.ctaHref && this.ctaHref.startsWith('#')) {
        const target = document.querySelector(this.ctaHref)

        if (target) {
          target.style.scrollMarginTop = '106px'
        }

        sessionStorage.setItem('nt-scroll-popup-cta', 'yes')

        this.$nextTick(() => this.close())
      }

      this.pushInternalContentGTM()
    },
    getGTMPayload(event, labelOverride) {
      const pageView = (window.dataLayer && window.dataLayer.find(e => e.event === 'page_view')) || {}
      return {
        event,
        label: labelOverride || (this.effectiveFabMode === 'icononly' ? 'icon-only' : this.fabLabel),
        name: this.fabMode === 'icononly' ? 'icon-only' : 'pill',
        section: 'pop-up',
        category: pageView.category || 'vazio',
        topic: pageView.topic || 'vazio',
        theme: pageView.theme || 'vazio',
      }
    },
    pushGTM() {
      if (window.dataLayer) {
        window.dataLayer.push(this.getGTMPayload('click'))
      }
    },
    pushInternalContentGTM() {
      if (window.dataLayer) {
        window.dataLayer.push(this.getGTMPayload('click', this.ctaText))
      }
    },
    pushCloseGTM() {
      if (window.dataLayer) {
        window.dataLayer.push(this.getGTMPayload('pop-up_close'))
      }
    }
  }
}
</script>
